import jQuery from 'jquery';

const selectors = {
    raceCardKey: '.js-RC-racecardKey',
    runnerRow: '.js-RC-runnerRow',
    horseListItems: '.js-horsesList',
    runnerWrapper: '.js-RC-runnerRowWrapper',
    diffusionPrice: '.js-diffusionPriceValue'
};

const effectsClasses = {
    activeSort: 'RC-racecardKey__item_sorted_asc',
    activeSortDesc: 'RC-racecardKey__item_rotate_arrow',
    reversedRunnerRow: 'js-RC-runnerRow__reversed'
};

const sortTypeValueProperties = {
    'js-RC-racecardKeyNo': 'data-order-no',
    'js-RC-racecardKeyDraw': 'data-order-draw',
    'js-RC-racecardKeyAge': 'data-order-age',
    'js-RC-racecardKeyWgt': 'data-order-wgt',
    'js-RC-racecardKeyJockey': 'data-order-jockey',
    'js-RC-racecardKeyTrainer': 'data-order-trainer',
    'js-RC-racecardKeyHorse': 'data-diffusion-horsename',
    'js-RC-racecardKeyOr': 'data-order-or',
    'js-RC-racecardKeyTs': 'data-order-ts',
    'js-RC-racecardKeyRpr': 'data-order-rpr',
    'js-RC-racecardKeyMr': 'data-order-mr',
    'js-RC-racecardKeyRace': 'data-order-datetime'
};

export const SORTING_KEYS = {
    PRICE: 'js-RC-racecardKeyPrice',
    ORDER_NUMBER: 'js-RC-racecardKeyNo',
    RACE: 'js-RC-racecardKeyRace'
};

const defaultSorting = (first, second) => {
    if (second.name !== first.name) {
        return (second.name > first.name) ? -1 : 1;
    }
    return first.defaultOrder - second.defaultOrder;
};

const sortNumerical = isDescending => (first, second) => {
    const firstValue = parseFloat(first.value);
    const secondValue = parseFloat(second.value);
    const isFirstEmptyOrNaN = !first.value || isNaN(first.value);
    const isSecondEmptyOrNaN = !second.value || isNaN(second.value);
    let result = null;

    if (
        !isFirstEmptyOrNaN &&
        !isSecondEmptyOrNaN &&
        first.value !== second.value
    ) {
        result = (firstValue - secondValue);
    } else if (isFirstEmptyOrNaN && !isSecondEmptyOrNaN) {
        result = -1;
    } else if (isSecondEmptyOrNaN && !isFirstEmptyOrNaN) {
        result = 1;
    }

    if (result !== null) {
        return result * (isDescending ? -1 : 1);
    }

    return defaultSorting(first, second);
};

function sortAlphabetical(first, second) {
    if (first.value !== second.value) {
        return (second.value > first.value) ? -1 : 1;
    }
    return defaultSorting(first, second);
}

function sortPrices(first, second) {
    if (first.value !== second.value) {
        return parseFloat(first.value) - parseFloat(second.value);
    }
    return defaultSorting(first, second);
}

function sortByCardsNo(first, second) {
    return first.defaultOrder - second.defaultOrder;
}

const getOrderingValue = selectorToSort => $item => $item.find(`[${selectorToSort}]`).attr(selectorToSort);

function getPriceOrderingValue($item) {
    const diffusionPrice = $item.find(selectors.diffusionPrice);
    const status = diffusionPrice.attr('data-diffusion-status');
    const decimal = diffusionPrice.attr('data-diffusion-decimal');
    const fractional = diffusionPrice.attr('data-diffusion-fractional');
    if (status === 'H') {
        return 9998; // '-'
    }
    if (fractional === 'SP') {
        return 9997; // 'SP'
    }
    if (decimal === undefined || decimal === '' || decimal === null || isNaN(decimal)) {
        return 9999; // no price
    }
    return parseFloat(decimal);
}

const hasPrices = () => (
    jQuery(selectors.runnerRow)
        .toArray()
        .map(jQuery)
        .map(getPriceOrderingValue)
        .some(value => value < 9997)
);

export default class CardSort {
    constructor() {
        this.sortStrategies = [
            {
                types: ['js-RC-racecardKeyHorse'],
                handler: this.alphabeticalSort.bind(this)
            },
            {
                types: [
                    'js-RC-racecardKeyJockey',
                    'js-RC-racecardKeyTrainer'
                ],
                handler: this.lastNameSort.bind(this)
            },
            {
                types: [
                    'js-RC-racecardKeyNo',
                    'js-RC-racecardKeyDraw',
                    'js-RC-racecardKeyAge',
                    'js-RC-racecardKeyWgt',
                    'js-RC-racecardKeyRace'
                ],
                handler: this.numericalSortAsc.bind(this)
            },
            {
                types: [
                    'js-RC-racecardKeyOr',
                    'js-RC-racecardKeyTs',
                    'js-RC-racecardKeyRpr',
                    'js-RC-racecardKeyMr'
                ],
                handler: this.numericalSortDesc.bind(this)
            },
            {
                types: ['js-RC-racecardKeyPrice'],
                handler: this.priceSort.bind(this)
            }
        ];
        this.currentSortingType = '';
        this.dynamicRunnersUpdatesHandler = this.dynamicRunnersUpdatesHandler.bind(this);
        this.userSettingsChangedHandler = this.userSettingsChangedHandler.bind(this);
        this.clickHandler = this.clickHandler.bind(this);
        this.priceUpdateHandler = this.priceUpdateHandler.bind(this);
        this.bookmakerChanged = this.bookmakerChanged.bind(this);
    }

    init({ defaultSort = SORTING_KEYS.ORDER_NUMBER } = {}) {
        this.bindEvents();
        this.markExcludedRunners();
        this.sortRunners(defaultSort);
    }

    /**
     * @private
     */
    bindEvents() {
        jQuery(selectors.raceCardKey)
            .on('click', this.clickHandler);
        jQuery(document)
            .on('RC.priceUpdatedToSort', this.priceUpdateHandler)
            .on('dynamicPricesUpdated', this.dynamicRunnersUpdatesHandler)
            .on('RC.runnerExcluded', this.dynamicRunnersUpdatesHandler)
            .on('userSettingsChanged.SETTINGS', this.userSettingsChangedHandler)
            .on('bookieChanged.BOOKMAKER', this.bookmakerChanged);
    }

    /**
     * @private
     */
    get runnerWrapper() {
        if (!this.runnerWrpr) {
            this.runnerWrpr = jQuery(selectors.runnerWrapper);
        }
        return this.runnerWrpr;
    }

    bookmakerChanged() {
        const isSortedByPrice =
            this.currentSortingType &&
            this.currentSortingType === SORTING_KEYS.PRICE;
        if (isSortedByPrice) {
            this.sort(getPriceOrderingValue, sortByCardsNo);
        }
    }

    /**
     * @private
     */
    markExcludedRunners() {
        const excludedRunners = JSON.parse(localStorage.getItem('cdt_exd')) || {};
        const raceId = jQuery('[data-card-race-id]').attr('data-card-race-id');
        const race = excludedRunners[raceId] || {};

        if (race.runners) {
            race.runners.forEach((id) => {
                const $runner = jQuery(`[data-ugc-runnerid=${id}]`);
                const $button = $runner.find('.js-RC-runner__excludeBtn');
                $runner.addClass('js-runnerExcludedRunner');
                $runner.addClass('RC-runnerRow_excluded');
                $button.addClass('RC-runnerButtons__item_active');
            });
        }
    }

    /**
     * @private
     */
    priceUpdateHandler() {
        const isSortedByPrice =
            this.currentSortingType &&
            this.currentSortingType === SORTING_KEYS.PRICE;

        if (isSortedByPrice) {
            this.priceSort();
        }
    }

    /**
     * @private
     */
    dynamicRunnersUpdatesHandler() {
        const currentSortingType = this.currentSortingType || SORTING_KEYS.ORDER_NUMBER;
        this.sortWithProperStrategy(currentSortingType);
    }

    /**
     * @private
     */
    userSettingsChangedHandler(event, userSettings) {
        const isAlreadySortedByPrice = SORTING_KEYS.PRICE === this.currentSortingType;
        const isAlreadySortedByNo = SORTING_KEYS.ORDER_NUMBER === this.currentSortingType;
        const isMyRatingChanged =
            jQuery(event.target.activeElement)
                .hasClass('js-RC-customizeSettings__switcher_myRatings');
        const isPriceSortingAvailable = !!jQuery('.js-RC-racecardKeyPrice').length;

        if (userSettings.orderByPrice && !isAlreadySortedByPrice && isPriceSortingAvailable) {
            this.sortRunners(SORTING_KEYS.PRICE);
            return;
        } else if (!userSettings.orderByPrice && !isAlreadySortedByNo) {
            this.sortRunners(SORTING_KEYS.ORDER_NUMBER);
            return;
        }

        if (isMyRatingChanged) {
            const isAlreadySortedByRpr = this.currentSortingType === 'js-RC-racecardKeyRpr';
            const isAlreadySortedByMr = this.currentSortingType === 'js-RC-racecardKeyMr';

            if (userSettings.myRatings && isAlreadySortedByRpr) {
                this.sortRunners('js-RC-racecardKeyMr');
            } else if (isAlreadySortedByMr) {
                this.sortRunners('js-RC-racecardKeyRpr');
            }
        }
    }

    /**
     * @private
     */
    clickHandler(event) {
        const clickableAreas = this.sortStrategies
            .map(({ types }) => types)
            .reduce((a, b) => a.concat(b), []);
        const closestSelector = clickableAreas.map(item => `.${item}`).join(',');
        const $targetSortElement = jQuery(event.target).closest(closestSelector);

        if ($targetSortElement.length) {
            const targetSortElementClasses = Array.from($targetSortElement[0].classList);
            const sortType = targetSortElementClasses
                .find(targetClass => clickableAreas.includes(targetClass));
            this.sortRunners(sortType);
        }
    }

    sortRunners(sortType) {
        const isReversingCurrentSorting = (this.currentSortingType === sortType);
        jQuery(`.${sortType}`).addClass(effectsClasses.activeSort);
        if (isReversingCurrentSorting) {
            jQuery(`.${sortType}`).toggleClass(effectsClasses.activeSortDesc);
            this.runnerWrapper.toggleClass(effectsClasses.reversedRunnerRow);
        } else {
            if (this.currentSortingType) {
                jQuery(`.${this.currentSortingType}`).removeClass(effectsClasses.activeSort);
                jQuery(`.${sortType}`).removeClass(effectsClasses.activeSortDesc);
            }
            this.runnerWrapper.removeClass(effectsClasses.reversedRunnerRow);
        }
        this.currentSortingType = sortType;
        this.sortWithProperStrategy(sortType);
    }

    /**
     * @private
     */
    sortWithProperStrategy(sortType) {
        this.sortStrategies
            .filter(({ types }) => (types.includes(sortType)))
            .forEach(({ handler }) => handler(sortType));
    }

    /**
     * @private
     */
    lastNameSort(sortType) {
        const selector = sortTypeValueProperties[sortType];
        const getValue = getOrderingValue(selector);
        const valueFn = $item => getValue($item).split(' ').pop();

        this.sort(valueFn, sortAlphabetical);
    }

    /**
     * @private
     */
    alphabeticalSort(sortType) {
        const selector = sortTypeValueProperties[sortType];

        this.sort(getOrderingValue(selector), sortAlphabetical);
    }

    /**
     * @private
     */
    numericalSortDesc(sortableType) {
        const selectorToSort = sortTypeValueProperties[sortableType];

        this.sort(getOrderingValue(selectorToSort), sortNumerical(true));
    }

    /**
     * @private
     */
    numericalSortAsc(sortableType) {
        const selectorToSort = sortTypeValueProperties[sortableType];

        this.sort(getOrderingValue(selectorToSort), sortNumerical(false));
    }

    /**
     * @private
     */
    priceSort() {
        this.sort(getPriceOrderingValue, hasPrices() ? sortPrices : sortByCardsNo);
    }

    /**
     * @private
     */
    sort(valueFn, orderFn) {
        jQuery(selectors.runnerRow)
            .toArray()
            .map(jQuery)
            .map($item => ({
                element: $item,
                name: $item.find(selectors.horseListItems).attr('data-diffusion-horsename'),
                defaultOrder: parseFloat($item.find('[data-order-no]').attr('data-order-no')),
                value: valueFn($item)
            }))
            .sort(orderFn)
            .forEach(this.setOrder, this);
    }

    /**
     * @private
     */
    setOrder({ element: sortedElement }, index) {
        const isReverseSorting = this.runnerWrapper.hasClass(effectsClasses.reversedRunnerRow);
        const isNonRunner = sortedElement.hasClass('js-runnerNonRunner');
        const isExcludedRunner = sortedElement.hasClass('js-runnerExcludedRunner');


        let offset = isReverseSorting ? 1000 : 1;
        if (isNonRunner) {
            offset = isReverseSorting ? 1 : 1000;
        } else if (isExcludedRunner) {
            offset = 500;
        }
        const sortingDirection = isReverseSorting ? -1 : 1;
        const order = (index + offset) * sortingDirection;

        sortedElement.css({
            order,
            '-ms-flex-order': order,
            '-webkit-order': order
        });
    }
}



// WEBPACK FOOTER //
// ./lib/js/modules/cardSorting/CardSort.js